// AdminEmailStore — Phase 6 Task 8 cross-room login email persistence.
//
// **Purpose**: AdminPage のヘッダ「ログイン中: <email>」表示用. cookie session
// は HttpOnly なので web から直接 email を取れない. かと言って `GET /admin/me`
// 等 backend に追加する必要は薄い (ヘッダ表示用 1 箇所のみ) ので、
// **LoginPage の MagicLinkForm submit 成功時**に入力された email を localStorage
// に保存しておく.
//
// 制約:
// - cross-device / cross-tab で magic link をクリックされた場合は **email が
//   localStorage に無い**. AdminPage 側で `null` を許容して email 表示を省略
//   する (= ヘッダから email を消す UX 劣化を許容).
// - logout 時に `clear()` を呼ぶ.
//
// Why singleton (not per-room): cross-room session 1 つにつき login email が
// 1 つ. ルームごとに分離する必要がない.

const KEY = 'oekaki:adminEmail';

/**
 * Minimal Storage shape this module uses (mirrors AdminTokenStore.ts).
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AdminEmailStore {
  /** Save the cross-room login email. Idempotent. No-op if `email` is empty. */
  save(email: string): void;
  /** Look up the saved email. Returns `null` if absent. */
  get(): string | null;
  /** Remove the saved email (logout). Idempotent. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${KEY}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

const createMemoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
};

export interface CreateAdminEmailStoreOptions {
  storage?: StorageLike | null;
}

export const createAdminEmailStore = (opts: CreateAdminEmailStoreOptions = {}): AdminEmailStore => {
  const explicit = opts.storage;
  const storage =
    explicit === null
      ? createMemoryStorage()
      : (explicit ?? probeBrowserStorage() ?? createMemoryStorage());

  return {
    save(email) {
      const trimmed = email.trim();
      if (trimmed.length === 0) return;
      try {
        storage.setItem(KEY, trimmed);
      } catch {
        // Quota / disabled storage. Swallow — UX 上は header の email 表示が
        // 出ないだけで、cookie session 自体は backend に残る.
      }
    },
    get() {
      try {
        return storage.getItem(KEY);
      } catch {
        return null;
      }
    },
    clear() {
      try {
        storage.removeItem(KEY);
      } catch {
        // ignore
      }
    },
  };
};
